import { useState, useEffect, useMemo, useRef } from 'react';
import { format, addDays, isSameDay } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { mealPlanService } from '@/services/mealPlan';
import { recipeService } from '@/services/recipe';
import { mealCompositionService } from '@/services/mealComposition';
import { useCurrentUserFamily } from './useCurrentUserFamily';
import { MEAL_PLANS, RECIPES, MEAL_COMPOSITIONS, getMealPlansQueryKey } from '@/constants/queryKeys';

export const useShoppingListDates = () => {
  const today = new Date();
  const sixDays = Array.from({ length: 6 }, (_, i) => addDays(today, i));
  
  const { familyId } = useCurrentUserFamily();
  
  // Fetch meal plans - using standardized query key format for cache consistency
  const { data: mealPlans = [], isLoading: isLoadingDates } = useQuery({
    queryKey: getMealPlansQueryKey(format(today, 'yyyy-MM-dd'), format(addDays(today, 5), 'yyyy-MM-dd'), familyId),
    queryFn: () => mealPlanService.getMealPlans(today, addDays(today, 5)),
    enabled: !!familyId,
    staleTime: 0, // Always fresh for shopping list
    gcTime: 24 * 60 * 60 * 1000, // 24 hours
  });

  // Fetch recipes and compositions for meal plan details
  const { data: recipes = [] } = useQuery({
    queryKey: [RECIPES],
    queryFn: recipeService.getRecipes,
    enabled: !!familyId,
  });

  const { data: compositions = [] } = useQuery({
    queryKey: [MEAL_COMPOSITIONS],
    queryFn: mealCompositionService.getMealCompositions,
    enabled: !!familyId,
  });

  // Create map of meal plans with details for each date - now supports multiple meals per day
  const mealPlansMap = mealPlans.reduce((acc, mealPlan) => {
    const dateKey = mealPlan.date;
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }

    let mealPlanDetail = null;
    if (mealPlan.recipeId) {
      const recipe = recipes.find(r => r.id === mealPlan.recipeId);
      if (recipe) {
        mealPlanDetail = {
          type: 'recipe' as const,
          id: recipe.id,
          name: recipe.title,
          isQuickRecipe: recipe.isQuickRecipe,
          mealType: mealPlan.mealType,
          authorFirstName: mealPlan.authorFirstName,
          imageUrl: recipe.image_url,
          storageImageUrl: recipe.storage_image_url,
          userImageUrl: recipe.user_image_url,
          storageImagePath: recipe.storage_image_path,
          userImagePath: recipe.user_image_path
        };
      }
    } else if (mealPlan.mealCompositionId) {
      const composition = compositions.find(c => c.id === mealPlan.mealCompositionId);
      if (composition) {
        mealPlanDetail = {
          type: 'composition' as const,
          id: composition.id,
          name: composition.name,
          recipeIds: composition.recipes.map(r => r.id),
          mealType: mealPlan.mealType,
          authorFirstName: mealPlan.authorFirstName
        };
      }
    }

    if (mealPlanDetail) {
      acc[dateKey].push(mealPlanDetail);
    }
    
    return acc;
  }, {} as Record<string, Array<{ 
    type: 'recipe' | 'composition';
    id: string;
    name: string;
    recipeIds?: string[];
    isQuickRecipe?: boolean;
    mealType: string;
    authorFirstName?: string;
    imageUrl?: string;
    storageImageUrl?: string;
    userImageUrl?: string;
    storageImagePath?: string;
    userImagePath?: string;
  }>>);

  // Get dates that have meal plans (memoized to prevent infinite re-renders)
  const availableDates = useMemo(() => {
    return sixDays.filter(date => {
      const dateKey = format(date, 'yyyy-MM-dd');
      return mealPlansMap[dateKey] && mealPlansMap[dateKey].length > 0;
    });
  }, [sixDays, mealPlansMap]);

  // State management with auto-selection
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const hasInitialized = useRef(false);

  // Auto-select all available dates on first load
  useEffect(() => {
    if (availableDates.length > 0 && !hasInitialized.current) {
      setSelectedDates([...availableDates]);
      hasInitialized.current = true;
    }
  }, [availableDates]);

  const handleDateToggle = (date: Date) => {
    setSelectedDates(prev => {
      const isSelected = prev.some(d => isSameDay(d, date));
      if (isSelected) {
        return prev.filter(d => !isSameDay(d, date));
      } else {
        return [...prev, date];
      }
    });
  };

  return {
    sixDays,
    availableDates,
    selectedDates,
    isLoadingDates,
    mealPlansMap,
    handleDateToggle
  };
};
